/**
 * Ask the case a question.
 *
 * A drawer on the case page rather than a screen of its own, because the answer
 * is only useful next to the rail and the clocks it is talking about — and
 * because an officer who has to leave the case to ask about it will check the
 * paper instead.
 *
 * Three things here are not decoration.
 *
 * **The standing note never goes away.** It is not dismissible and it is not
 * behind an info icon. The answers are drawn from a record that is itself being
 * built, and an officer acting on one without checking has been let down by the
 * screen rather than by the model.
 *
 * **Every citation opens.** A chip that could not be clicked would be asking the
 * officer to take the grounding on trust, which is the thing this whole feature
 * is arranged against. Rules open the rule drawer with its unverified marker;
 * documents land on the cited block; events scroll the timeline to the act;
 * pages open the bundle at the page OCR read it from.
 *
 * **A refusal says which kind of refusal it is.** "The record does not show
 * this" and "the answer was rejected because it cited something it was never
 * given" are different facts, and the second one is about the answer rather than
 * about the case. The server distinguishes them; so does this.
 *
 * `AI_MODE=off` leaves the panel on screen, greyed, with the reason. A blocked
 * action is never hidden (rule 3), and nothing else on the case degrades.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { ChatMessageView, CitationView } from '../api/client'
import { ErrorEnvelope, RuleDrawer } from './bits'

/**
 * Where a citation lands on the case page behind the drawer.
 *
 * Three of the six kinds point at something already on this page rather than at
 * another route: an event on the timeline, a period in the Periods panel, and a
 * document's own row on the rail — which is where a *skipped* document's reason
 * is, and a skipped document has no draft to open.
 */
const PAGE_ANCHOR: Record<string, (anchor: string) => string> = {
  event: (anchor) => `event-${anchor}`,
  clock: (anchor) => `clock-${anchor}`,
  rail: (anchor) => `doc-${anchor}`,
}

export function CaseChat({ caseId, onClose }: { caseId: string; onClose: () => void }) {
  const client = useQueryClient()
  const [question, setQuestion] = useState('')
  const bottom = useRef<HTMLDivElement | null>(null)

  const panel = useQuery({
    queryKey: ['chat', caseId],
    queryFn: () => api.getCaseChat(caseId),
  })

  // The papers on the file, for turning a page citation into the scan it was
  // read from. OCR has fabricated text on a real page (19 Aug 2026), so every
  // page chip must open the actual scan — the officer's eye is the mitigation.
  const papers = useQuery({
    queryKey: ['source-files', caseId],
    queryFn: () => api.listSourceFiles(caseId),
  })
  const jobToFile: Record<string, string> = {}
  for (const row of papers.data?.files ?? []) {
    if (row.ingestion_job_id && !row.removed_at) jobToFile[row.ingestion_job_id] = row.id
  }

  /* **The question leaves the box the moment it is asked.**
   *
   * It cleared on success, so an officer pressed Enter and their own sentence
   * sat in the textarea for the length of a model call — several seconds —
   * while a *"Reading the case record…"* line appeared beneath it. It read as
   * though nothing had been sent, and the obvious response is to press Enter
   * again.
   *
   * So the box empties on send and the question is echoed into the log at
   * once, where it belongs. This is not the diff gate's territory: the echo is
   * the officer's own words being shown back to them, not the machine changing
   * something under them (rule 2). It carries no id and no citations, and it
   * is replaced by the server's own row the moment the answer lands.
   *
   * **A failed ask keeps it.** If the model is unreachable the question stays
   * in the log with the error beneath it, because a question that vanishes on
   * failure is a question the officer has to remember and retype. */
  const [pending, setPending] = useState<string | null>(null)
  const ask = useMutation({
    mutationFn: (text: string) => api.askCase(caseId, { question: text }),
    onMutate: (text: string) => {
      setQuestion('')
      setPending(text)
    },
    onSuccess: () => {
      setPending(null)
      void client.invalidateQueries({ queryKey: ['chat', caseId] })
    },
  })

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
    // `pending` too, or the echoed question can land below the fold on a log
    // that already fills the panel — which is the same complaint as it not
    // appearing at all.
  }, [panel.data?.messages?.length, ask.isPending, pending])

  const available = panel.data?.available ?? false
  const messages = panel.data?.messages ?? []

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer wide" role="dialog" aria-label="Ask about this case">
        <header>
          <div>
            <div>Ask about this case</div>
            <div className="small muted">
              {panel.data ? <>as at {panel.data.as_of}</> : 'reading…'}
            </div>
          </div>
          <button type="button" className="quiet" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="body">
          {/* Not dismissible, and above the answers rather than under them. */}
          <p className="standing-note" data-testid="chat-standing-note">
            {panel.data?.standing_note ??
              'Answers are drawn from this case’s record and the rule corpus. Verify before relying.'}
          </p>

          {panel.error ? <ErrorEnvelope error={panel.error} /> : null}

          {panel.data && !available ? (
            <p className="envelope" data-testid="chat-unavailable">
              {panel.data.unavailable_reason}
            </p>
          ) : null}

          <div className="chat-log" data-testid="chat-log">
            {messages.length === 0 ? (
              <div className="empty">
                Nothing has been asked on this case yet.
              </div>
            ) : (
              messages.map((message) => (
                <Message
                  key={message.id}
                  caseId={caseId}
                  message={message}
                  onClose={onClose}
                  jobToFile={jobToFile}
                />
              ))
            )}
            {pending !== null ? (
              <div className="chat-turn user" data-testid="chat-pending">
                <div className="bubble deva">{pending}</div>
              </div>
            ) : null}
            {ask.isPending ? (
              <div className="chat-turn assistant" data-testid="chat-thinking">
                <div className="bubble thinking small muted">Reading the case record…</div>
              </div>
            ) : null}
            <div ref={bottom} />
          </div>

          <ErrorEnvelope error={ask.error} />

          <form
            className="chat-ask"
            onSubmit={(event) => {
              event.preventDefault()
              const text = question.trim()
              if (text && available) ask.mutate(text)
            }}
          >
            <label className="small muted" htmlFor="chat-question">
              Your question — Hindi or English
            </label>
            {/* **Enter asks.** It put the cursor on a second line, which is a
                text editor's answer in a box nobody writes a paragraph in — an
                officer types a question and presses Enter, and got a taller
                box. Shift+Enter still breaks a line for the rare long one, and
                that is the convention every messaging app has taught. */}
            <textarea
              id="chat-question"
              className="deva"
              rows={2}
              value={question}
              disabled={!available || ask.isPending}
              placeholder="e.g. when does the reply period expire?"
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || event.shiftKey) return
                event.preventDefault()
                const text = question.trim()
                if (text && available && !ask.isPending) ask.mutate(text)
              }}
            />
            {/* Greyed with the reason on screen, never removed. */}
            <div className="chat-send">
              <button
                type="submit"
                className="btn primary"
                disabled={!available || ask.isPending || !question.trim()}
              >
                Ask
              </button>
              {/* Said once, where the hands are. A keystroke that does something
                  new is worth one line the first time an officer meets it. */}
              {available ? (
                <span className="small faint">Enter asks · Shift+Enter for a new line</span>
              ) : null}
              {!available ? (
                <span className="why small" data-testid="chat-ask-blocked">
                  {panel.data?.unavailable_reason ?? 'Chat is unavailable.'}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </aside>
    </>
  )
}

function Message({
  caseId,
  message,
  onClose,
  jobToFile,
}: {
  caseId: string
  message: ChatMessageView
  onClose: () => void
  jobToFile: Record<string, string>
}) {
  const citations = message.citations ?? []
  const [showAll, setShowAll] = useState(false)
  /* **One caveat per thing, not one per block.** Five blocks of the same
     noting sheet produced five caveat lines, identical but for a block number,
     each one a full sentence. What an officer needs to know is *which
     document* is not settled and *why* — the block numbers are in the chips
     above. Grouped by source and reason, counted. */
  const caveats = Object.values(
    citations.reduce<Record<string, { key: string; on: string; text: string; n: number }>>(
      (acc, citation) => {
        const text = [citation.provenance, citation.disposition].filter(Boolean).join(' · ')
        if (!text) return acc
        const on = citation.label.split(' — ')[0] ?? citation.label
        const key = `${on}::${text}`
        const seen = acc[key]
        if (seen) seen.n += 1
        else acc[key] = { key, on, text, n: 1 }
        return acc
      },
      {},
    ),
  )
  const grounding = (message.grounding ?? {}) as Record<string, unknown>
  const forced = grounding['forced'] === true
  const forcedReason = typeof grounding['forced_reason'] === 'string' ? grounding['forced_reason'] : null
  const nearest = Array.isArray(grounding['nearest'])
    ? (grounding['nearest'] as CitationView[])
    : []
  const mode = typeof grounding['mode'] === 'string' ? grounding['mode'] : null

  return (
    <div
      className={`chat-turn ${message.role}${message.refused ? ' refused' : ''}`}
      data-testid={`chat-${message.role}`}
      data-confidence={message.confidence ?? undefined}
    >
      {/* A refusal is rendered as one, on reload as well as when it arrives.
          An unsupported answer shown in the same style as a grounded one is the
          defect the citation check exists to prevent, arriving a day later. */}
      {message.refused ? (
        <div className="small muted" data-testid="chat-refused">
          Not answered from the record
        </div>
      ) : null}
      {/* **A bubble, because this is a conversation.** It was a left rule and a
          block of text for both sides, so a question and an answer were the
          same shape in two shades of grey and a log of six turns read as one
          long column. Archit, 5 Sep: *"clean the way questions and answers
          appear, like a real chatbot / messaging app."* The question sits
          right and tinted; the answer sits left on the surface, wider, because
          one is a line and the other is the work. */}
      <div className={message.role === 'user' ? 'bubble deva' : 'bubble deva chat-answer'}>
        {message.content}
      </div>

      {/* **Four sources, then a count.** A question about deadlines cites every
          clock and every block it read — nine or ten chips, each a full
          sentence, three lines deep under a three-line answer. The citations
          are not optional: an answer without them is what this product refuses
          to be. But an officer checks one or two, and a wall of ten is read as
          decoration, which is worse than four and a way to see the rest. */}
      {citations.length > 0 ? (
        <div className="chat-sources">
          <span className="small muted">Drawn from</span>{' '}
          {citations.slice(0, showAll ? citations.length : 4).map((citation, index) => (
            <Source
              key={`${citation.kind}-${citation.id}-${citation.anchor}-${index}`}
              caseId={caseId}
              citation={citation}
              onNavigate={onClose}
              jobToFile={jobToFile}
            />
          ))}
          {citations.length > 4 && !showAll ? (
            <button type="button" className="link" onClick={() => setShowAll(true)}>
              and {citations.length - 4} more
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Which grounding mode answered — whole case read, or the retrieval
          fallback above the size ceiling — so nobody later wonders why an
          answer looked thin. */}
      {message.role === 'assistant' && mode ? (
        <div
          className="small faint"
          data-testid="chat-mode"
          /* On the chip rather than on its own line: which retrieval path ran
             is a fact about the machine, and it was taking a line under every
             answer to say the ordinary thing. It still says the unusual one —
             that the case was too large to read whole — because that changes
             how much weight the answer carries. */
          title={
            mode === 'whole-case'
              ? 'The whole case record was read for this answer.'
              : 'This case exceeded the whole-case size ceiling; full-text search chose what was read.'
          }
        >
          {mode === 'whole-case'
            ? 'Read from the whole record'
            : 'Too large to read whole — searched'}
        </div>
      ) : null}

      {/* What is not settled about the text an answer was drawn from: our own
          unsourced draft, statutory text nobody has checked, a page the officer
          set aside. Beside the answer, not behind a click. */}
      {caveats.length > 0 ? (
        <ul className="chat-caveats" data-testid="chat-caveats">
          {caveats.map((caveat) => (
            <li key={caveat.key} className="small">
              <span className="unverified">NOT SETTLED</span>{' '}
              <span className="mono">{caveat.on}</span>
              {caveat.n > 1 ? <span className="muted"> ×{caveat.n}</span> : null} — {caveat.text}
            </li>
          ))}
        </ul>
      ) : null}

      {/* Why it was refused, in the officer's own screen rather than in a log. */}
      {forced && forcedReason ? (
        <div className="blocked" data-testid="chat-forced-reason">
          <div>
            <div className="why">{forcedReason}</div>
            <div className="small muted">
              The answer was not shown. Nothing about this case has changed.
            </div>
          </div>
        </div>
      ) : null}

      {nearest.length > 0 ? (
        <div className="chat-sources" data-testid="chat-nearest">
          <span className="small muted">The record does contain</span>{' '}
          {nearest.map((citation, index) => (
            <Source
              key={`near-${citation.kind}-${citation.id}-${citation.anchor}-${index}`}
              caseId={caseId}
              citation={citation}
              onNavigate={onClose}
              jobToFile={jobToFile}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * One citation chip, opened the way its kind is opened.
 *
 * A rule reuses the existing citation chip, so the drawer behind it — and the
 * unverified marker in that drawer — is the same one every other screen shows.
 * There is deliberately not a second rendering of a rule in this file.
 */
function RuleChip({ citation }: { citation: CitationView }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        className="citation"
        data-testid="citation-rule"
        onClick={() => setOpen(true)}
      >
        {citation.label}
      </button>
      {open ? (
        <RuleDrawer citation={citation.id} refs={[citation.id]} onClose={() => setOpen(false)} />
      ) : null}
    </>
  )
}

function Source({
  caseId,
  citation,
  onNavigate,
  jobToFile,
}: {
  caseId: string
  citation: CitationView
  onNavigate: () => void
  jobToFile: Record<string, string>
}) {
  if (citation.kind === 'rule') {
    // **The label, not the ref.** The server puts `(UNVERIFIED)` on it, and this
    // is the one surface in the product where unverified statutory Hindi is
    // quoted in prose to be read and acted on — so the marker belongs beside the
    // quotation, not only in the drawer behind it (stop list 10).
    return <RuleChip citation={citation} />
  }

  if (citation.kind === 'document' && citation.document_type) {
    const anchor = citation.anchor ? `&b=${encodeURIComponent(citation.anchor)}` : ''
    return (
      <Link
        className="citation"
        data-testid="citation-document"
        onClick={onNavigate}
        to={`/cases/${caseId}/documents/${citation.document_type}?v=${citation.id}${anchor}`}
      >
        {citation.label}
      </Link>
    )
  }

  if (citation.kind === 'page' && citation.job_id) {
    // A per-file paper's page opens the scan itself — the officer's eye on
    // the actual paper is the only mitigation for OCR text that may not be
    // faithful to it. A bundle page keeps the page map, which shows the scan
    // beside its segments.
    const fileId = jobToFile[String(citation.job_id)]
    if (fileId) {
      return (
        <a
          className="citation"
          data-testid="citation-page"
          href={api.sourceFilePath(caseId, fileId)}
          target="_blank"
          rel="noreferrer"
        >
          {citation.label}
        </a>
      )
    }
    const anchor = citation.anchor ? `?p=${encodeURIComponent(citation.anchor)}` : ''
    return (
      <Link
        className="citation"
        data-testid="citation-page"
        onClick={onNavigate}
        to={`/cases/${caseId}/bundles/${citation.job_id}${anchor}`}
      >
        {citation.label}
      </Link>
    )
  }

  const anchorFor = PAGE_ANCHOR[citation.kind]
  if (anchorFor) {
    return (
      <button
        type="button"
        className="citation"
        data-testid={`citation-${citation.kind}`}
        onClick={() => {
          onNavigate()
          // The target is on the page behind this drawer, so closing is part of
          // following the citation rather than a separate step.
          window.setTimeout(() => {
            const target = document.querySelector(
              `[data-testid="${anchorFor(citation.anchor ?? citation.id)}"]`,
            )
            target?.scrollIntoView({ block: 'center' })
            target?.classList.add('cited')
          }, 0)
        }}
      >
        {citation.label}
      </button>
    )
  }

  // A handle whose kind is known but whose route is not — a page citation from
  // before job ids travelled, say. Shown, not hidden: the label still tells the
  // officer where to look by hand.
  return <span className="chip">{citation.label}</span>
}
